var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { property, state, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { live } from 'lit/directives/live.js';
/**
 * @element kr-auto-suggest
 *
 * @property {string} label - Label text for the input
 * @property {string} name - Name for form submission
 * @property {string} value - Current input value
 * @property {string} placeholder - Placeholder text
 * @property {boolean} disabled - Whether the input is disabled
 * @property {boolean} readonly - Whether the input is readonly
 * @property {boolean} required - Whether the input is required
 * @property {string} hint - Helper text displayed below the input
 * @property {KRAutoSuggestOption[]} options - Array of suggestion options
 * @property {Function} fetch - Function that returns a promise of options
 *
 * @fires change - Fired when the input value changes
 * @fires select - Fired when a suggestion is selected
 *
 * @slot - Not used
 */
export class KRAutoSuggest extends LitElement {
    constructor() {
        super();
        // The value the user last typed or picked. While dirty, an external `value` set is reverted to
        // this (see willUpdate) so a parent re-binding from the server can't overwrite an unsaved edit.
        this._dirtyValue = '';
        this._requestId = 0;
        this.label = '';
        this.name = '';
        this.value = '';
        this.placeholder = '';
        this.disabled = false;
        this.readonly = false;
        this.required = false;
        this.hint = '';
        this.options = [];
        this.fetch = null;
        /**
         * Whether the user has changed this field since it was last loaded or marked pristine. Set on
         * user input, cleared by markPristine(). Used by <kr-form> to collect changed fields.
         */
        this.dirty = false;
        /**
         * Whether a <kr-form> save carrying this field's edit is in flight. Set by markSaving(), cleared
         * by markSaved()/markSaveFailed().
         */
        this.saving = false;
        this._isOpen = false;
        this._highlightedIndex = -1;
        this._touched = false;
        this._handleDocumentClick = (e) => {
            const path = e.composedPath();
            if (!path.includes(this)) {
                this._isOpen = false;
            }
        };
        this._handleInvalid = (e) => {
            e.preventDefault();
            this._touched = true;
        };
        this._internals = this.attachInternals();
    }
    connectedCallback() {
        super.connectedCallback();
        // Marks this as a krubble form field so <kr-form> can find its controls (.kr-form-field).
        this.classList.add('kr-form-field');
        document.addEventListener('click', this._handleDocumentClick);
        this.addEventListener('invalid', this._handleInvalid);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('click', this._handleDocumentClick);
        this.removeEventListener('invalid', this._handleInvalid);
    }
    firstUpdated() {
        this._updateValidity();
    }
    willUpdate(changedProperties) {
        // This field's own save-mode, else the enclosing <kr-form>'s. Unset either way means manual.
        let saveMode = this.saveMode;
        if (!saveMode) {
            saveMode = this.closest('kr-form')?.saveMode;
        }
        // While the user has an unsaved edit, an external `value` set (e.g. a parent re-binding from a
        // server response that predates the edit) would overwrite it. Revert to the user's value. The
        // field accepts external values again once the edit is saved (markSaving clears dirty) or reset.
        if (changedProperties.has('value') && this.dirty && this.value !== this._dirtyValue && saveMode === 'autosave') {
            this.value = this._dirtyValue;
        }
    }
    updated(changedProperties) {
        if (changedProperties.has('required') || changedProperties.has('value')) {
            this._updateValidity();
        }
        if (changedProperties.has('options') && this._isOpen) {
            this._positionDropdown();
        }
    }
    get form() {
        return this._internals.form;
    }
    get validity() {
        return this._internals.validity;
    }
    get validationMessage() {
        return this._internals.validationMessage;
    }
    checkValidity() {
        return this._internals.checkValidity();
    }
    reportValidity() {
        return this._internals.reportValidity();
    }
    formResetCallback() {
        this.value = '';
        this._touched = false;
        this.dirty = false;
        this.saving = false;
        this._isOpen = false;
        this._highlightedIndex = -1;
        this._internals.setFormValue('');
        this._internals.setValidity({});
    }
    // Clears the dirty flag so the field is no longer reported as changed.
    markPristine() {
        this.dirty = false;
    }
    // <kr-form> calls this when a save carrying this field's edit starts. Clearing dirty here means a
    // further edit during the request is tracked as new and goes out in the next save.
    markSaving() {
        this.saving = true;
        this.dirty = false;
    }
    // <kr-form> calls this when the save succeeded. If the user edited mid-flight the field is dirty
    // again and stays that way.
    markSaved() {
        this.saving = false;
    }
    // <kr-form> calls this when the save failed. Restore dirty so the next change retries the edit.
    markSaveFailed() {
        this.saving = false;
        this.dirty = true;
        this._dirtyValue = this.value;
    }
    formStateRestoreCallback(state) {
        this.value = state;
    }
    _updateValidity() {
        if (this._input) {
            this._internals.setValidity(this._input.validity, this._input.validationMessage);
        }
    }
    _fetch() {
        if (!this.fetch) {
            return;
        }
        this._requestId++;
        const requestId = this._requestId;
        this.fetch(this.value).then((options) => {
            if (requestId === this._requestId) {
                this.options = options;
            }
        }).catch((error) => {
            console.error('kr-auto-suggest: fetch failed', error);
        });
    }
    _handleInput(e) {
        this.value = e.target.value;
        this.dirty = true;
        this._dirtyValue = this.value;
        this._isOpen = true;
        this._highlightedIndex = -1;
        this._positionDropdown();
        this._internals.setFormValue(this.value);
        this._internals.setValidity(this._input.validity, this._input.validationMessage);
        this.dispatchEvent(new Event('change', {
            bubbles: true,
            composed: true
        }));
        this._fetch();
    }
    _positionDropdown() {
        requestAnimationFrame(() => {
            const dropdown = this.shadowRoot?.querySelector('.dropdown');
            if (!dropdown) {
                return;
            }
            const inputRect = this._input.getBoundingClientRect();
            const spaceBelow = window.innerHeight - inputRect.bottom - 4 - 8;
            const spaceAbove = inputRect.top - 4 - 8;
            dropdown.style.left = inputRect.left + 'px';
            dropdown.style.width = inputRect.width + 'px';
            // Open above the input if there's more room above than below
            if (spaceAbove > spaceBelow) {
                dropdown.style.top = '';
                dropdown.style.bottom = (window.innerHeight - inputRect.top + 4) + 'px';
                dropdown.style.maxHeight = spaceAbove + 'px';
            }
            else {
                dropdown.style.bottom = '';
                dropdown.style.top = inputRect.bottom + 4 + 'px';
                dropdown.style.maxHeight = spaceBelow + 'px';
            }
        });
    }
    _handleFocus() {
        this._isOpen = true;
        this._positionDropdown();
        this._fetch();
    }
    _handleBlur() {
        this._touched = true;
        this._internals.setValidity(this._input.validity, this._input.validationMessage);
        // Delay to allow click on option
        setTimeout(() => {
            this._isOpen = false;
            this._highlightedIndex = -1;
        }, 200);
    }
    _handleKeyDown(e) {
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                this._isOpen = true;
                this._highlightedIndex = Math.min(this._highlightedIndex + 1, this.options.length - 1);
                this._scrollToHighlighted();
                break;
            case 'ArrowUp':
                e.preventDefault();
                if (this._highlightedIndex === -1) {
                    this._isOpen = true;
                    this._highlightedIndex = this.options.length - 1;
                }
                else {
                    this._highlightedIndex = Math.max(this._highlightedIndex - 1, 0);
                }
                this._scrollToHighlighted();
                break;
            case 'Enter':
                if (this._highlightedIndex >= 0 && this.options[this._highlightedIndex]) {
                    e.preventDefault();
                    this._selectOption(this.options[this._highlightedIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                this._isOpen = false;
                this._highlightedIndex = -1;
                break;
            case 'Tab':
                this._isOpen = false;
                this._highlightedIndex = -1;
                break;
        }
    }
    _scrollToHighlighted() {
        this.updateComplete.then(() => {
            const container = this.shadowRoot?.querySelector('.dropdown');
            const highlighted = this.shadowRoot?.querySelector('.option--highlighted');
            if (container && highlighted) {
                const containerRect = container.getBoundingClientRect();
                const highlightedRect = highlighted.getBoundingClientRect();
                if (highlightedRect.bottom > containerRect.bottom) {
                    highlighted.scrollIntoView({ block: 'nearest' });
                }
                else if (highlightedRect.top < containerRect.top) {
                    highlighted.scrollIntoView({ block: 'nearest' });
                }
            }
        });
    }
    _selectOption(option) {
        if (option.disabled) {
            return;
        }
        this.value = option.value;
        this.dirty = true;
        this._dirtyValue = this.value;
        this._isOpen = false;
        this._highlightedIndex = -1;
        this._internals.setFormValue(this.value);
        this.dispatchEvent(new CustomEvent('select', {
            detail: {
                value: option.value,
                option: option
            },
            bubbles: true,
            composed: true,
        }));
        this.dispatchEvent(new Event('change', {
            bubbles: true,
            composed: true
        }));
    }
    _handleClear() {
        this.value = '';
        this.dirty = true;
        this._dirtyValue = this.value;
        this._internals.setFormValue(this.value);
        this.dispatchEvent(new Event('change', {
            bubbles: true,
            composed: true
        }));
        this._input?.focus();
    }
    _handleOptionMouseEnter(e, index) {
        this._highlightedIndex = index;
    }
    _renderOption(option, index) {
        return html `
      <button
        class=${classMap({
            option: true,
            'option--highlighted': index === this._highlightedIndex,
        })}
        type="button"
        role="option"
        aria-selected=${index === this._highlightedIndex}
        ?disabled=${option.disabled}
        @click=${(e) => this._selectOption(option)}
        @mouseenter=${(e) => this._handleOptionMouseEnter(e, index)}
      >
        ${option.label || option.value}
      </button>
    `;
    }
    render() {
        let footer = nothing;
        if (this._touched && this._input && !this._input.validity.valid) {
            footer = html `<div class="validation-message">${this._input.validationMessage}</div>`;
        }
        else if (this.hint) {
            footer = html `<div class="hint">${this.hint}</div>`;
        }
        return html `
      <div class="wrapper">
        ${this.label
            ? html `
            <label>
              ${this.label}
              ${this.required ? html `<span class="required">*</span>` : nothing}
            </label>
          `
            : nothing}

        <div class="input-wrapper">
          <input
            type="text"
            .value=${live(this.value)}
            placeholder=${this.placeholder}
            ?disabled=${this.disabled}
            ?readonly=${this.readonly}
            ?required=${this.required}
            name=${this.name}
            autocomplete="off"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded=${this._isOpen}
            aria-controls="dropdown"
            class=${classMap({
            'input--invalid': this._touched && this._input && !this._input.validity.valid,
        })}
            @input=${this._handleInput}
            @blur=${this._handleBlur}
            @focus=${this._handleFocus}
            @keydown=${this._handleKeyDown}
          />
          <div class="icon-wrapper">
            ${this.value && !this.disabled && !this.readonly
            ? html `
                <svg
                  class="clear"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-label="Clear"
                  @click=${this._handleClear}
                >
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              `
            : nothing}
            ${!this.value && !this.disabled && !this.readonly
            ? html `
                <svg
                  class="search-icon"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                </svg>
              `
            : nothing}
          </div>
        </div>

        <div
          id="dropdown"
          role="listbox"
          class=${classMap({
            dropdown: true,
            'dropdown--open': this._isOpen && this.options.length > 0,
        })}
        >
          ${this.options.map((option, index) => this._renderOption(option, index))}
        </div>

        ${footer}
      </div>
    `;
    }
}
KRAutoSuggest.styles = css `
    :host {
      display: block;
      font-family: inherit;
    }

    .wrapper {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    label {
      font-size: 14px;
      font-weight: 500;
      color: black;
    }

    .required {
      color: var(--kr-text-field-required-color, #ef4444);
      margin-left: 0.125rem;
    }

    .input-wrapper {
      position: relative;
      display: flex;
      align-items: center;
    }

    input {
      flex: 1;
      width: 100%;
      padding: 10px 32px 10px 12px;
      font-size: 14px;
      font-family: inherit;
      border: 1px solid var(--kr-text-field-border-color, #0000003d);
      border-radius: 8px;
      background-color: var(--kr-text-field-bg, #fff);
      color: var(--kr-text-field-color, #111827);
      transition: border-color 0.2s, box-shadow 0.2s;
      box-sizing: border-box;
    }

    input:focus {
      outline: none;
      border-color: var(--kr-text-field-focus-border-color, #163052);
      box-shadow: 0 0 0 3px var(--kr-text-field-focus-ring-color, rgba(22, 48, 82, 0.1));
    }

    input:disabled {
      background-color: var(--kr-text-field-disabled-bg, #f3f4f6);
      color: var(--kr-text-field-disabled-color, #9ca3af);
      cursor: not-allowed;
    }

    input:read-only {
      background-color: var(--kr-text-field-disabled-bg, #f3f4f6);
      cursor: default;
    }

    input.input--invalid {
      border-color: var(--kr-text-field-error-border-color, #ef4444);
    }

    input.input--invalid:focus {
      box-shadow: 0 0 0 3px var(--kr-text-field-error-ring-color, rgba(239, 68, 68, 0.1));
    }

    input::placeholder {
      color: var(--kr-text-field-placeholder-color, #9ca3af);
    }

    .icon-wrapper {
      position: absolute;
      right: 8px;
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .clear {
      width: 20px;
      height: 20px;
      color: var(--kr-text-muted, #6b7280);
      cursor: pointer;
      transition: color 0.15s ease;
    }

    .clear:hover {
      color: var(--kr-text, #000000);
    }

    .search-icon {
      width: 20px;
      height: 20px;
      color: var(--kr-text-muted, #6b7280);
      pointer-events: none;
    }

    /* Dropdown */
    .dropdown {
      position: fixed;
      z-index: 10000;
      overflow-y: auto;
      background: white;
      border: 1px solid #9ba7b6;
      border-radius: 8px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
      padding: 8px 0;
      box-sizing: border-box;
      display: none;
    }

    .dropdown--open {
      display: block;
    }

    /* Options */
    .option {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      width: 100%;
      padding: 8px 12px;
      background: none;
      border: none;
      font-size: 14px;
      line-height: 1.5;
      font-family: inherit;
      color: var(--kr-text, #000000);
      text-align: left;
      cursor: pointer;
      transition: background 0.15s ease;
    }

    .option:hover:not(:disabled) {
      background: var(--kr-select-option-hover-bg, #f3f4f6);
    }

    .option--highlighted {
      background-color: var(--kr-select-option-hover-bg, #f3f4f6);
    }

    .option:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    
    .hint {
      font-size: 0.75rem;
      color: var(--kr-text-field-helper-color, #6b7280);
    }

    .validation-message {
      font-size: 0.75rem;
      color: var(--kr-text-field-error-color, #ef4444);
    }
  `;
KRAutoSuggest.formAssociated = true;
__decorate([
    property({ type: String })
], KRAutoSuggest.prototype, "label", void 0);
__decorate([
    property({ type: String })
], KRAutoSuggest.prototype, "name", void 0);
__decorate([
    property({ type: String })
], KRAutoSuggest.prototype, "value", void 0);
__decorate([
    property({ type: String })
], KRAutoSuggest.prototype, "placeholder", void 0);
__decorate([
    property({ type: Boolean })
], KRAutoSuggest.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean })
], KRAutoSuggest.prototype, "readonly", void 0);
__decorate([
    property({ type: Boolean })
], KRAutoSuggest.prototype, "required", void 0);
__decorate([
    property({ type: String })
], KRAutoSuggest.prototype, "hint", void 0);
__decorate([
    property({ type: Array })
], KRAutoSuggest.prototype, "options", void 0);
__decorate([
    property({ attribute: false })
], KRAutoSuggest.prototype, "fetch", void 0);
__decorate([
    property({
        type: Boolean,
        reflect: true,
    })
], KRAutoSuggest.prototype, "dirty", void 0);
__decorate([
    property({ attribute: 'save-mode' })
], KRAutoSuggest.prototype, "saveMode", void 0);
__decorate([
    property({
        type: Boolean,
        reflect: true,
    })
], KRAutoSuggest.prototype, "saving", void 0);
__decorate([
    state()
], KRAutoSuggest.prototype, "_isOpen", void 0);
__decorate([
    state()
], KRAutoSuggest.prototype, "_highlightedIndex", void 0);
__decorate([
    state()
], KRAutoSuggest.prototype, "_touched", void 0);
__decorate([
    query('input')
], KRAutoSuggest.prototype, "_input", void 0);
// Guarded registration: no-op if the tag is already defined, so a second copy of krubble on
// the page (e.g. the Operations shell loads krubble globally while an embedded app bundled its
// own copy) doesn't throw. First definition wins.
if (!customElements.get('kr-auto-suggest')) {
    customElements.define('kr-auto-suggest', KRAutoSuggest);
}
//# sourceMappingURL=auto-suggest.js.map